import { useMemo } from 'react';

import { useDataProvider } from '@work4all/data';

import {
  createEntityRecordMatcherResult,
  createSourceEntityRecordMatcherRequest,
  resolveEntityRecordMatcherOptions,
} from '../utils';

import {
  UseEntityRecordMatcherProps,
  UseEntityRecordMatcherReturn,
} from './types';

export const useEntityRecordMatcher = <TData>({
  sourceEntity,
  sourceRecordIds,
  targetEntity,
  ...rest
}: UseEntityRecordMatcherProps): UseEntityRecordMatcherReturn<TData> => {
  const options = useMemo(() => {
    return resolveEntityRecordMatcherOptions<TData>({
      targetEntity,
      options: rest.options,
    });
  }, [targetEntity, rest]);

  const request = useMemo(() => {
    return createSourceEntityRecordMatcherRequest<TData>({
      sourceEntity,
      sourceRecordIds,
      targetEntity,
      config: options.config,
      skip: options.skip,
    });
  }, [
    options.config,
    options.skip,
    sourceEntity,
    sourceRecordIds,
    targetEntity,
  ]);

  const response = useDataProvider<Record<string, unknown>>(
    request,
    options.skip
  );

  const result = useMemo(() => {
    return createEntityRecordMatcherResult<TData>({
      response,
      sourceEntity,
      config: options.config,
      skip: options.skip,
    });
  }, [options.config, response, sourceEntity, options.skip]);

  return result;
};
